import { CorrectLens } from "ramda";
import api, { SpreadUpdateFinancialValueModel } from "src/api";
import { Comment } from "src/commentView/types";
import { getUserId } from "src/login/selector";
import actionCreatorFactory from "typescript-fsa";
import { asyncFactory, thunkToAction } from "typescript-fsa-redux-thunk";
import { isCreditLensFeatureEnabled } from "../../../configState/selector";
import {
  BriefSpreadStatus,
  SPREAD_STATUSES
} from "../../../documentList/types";
import { State } from "../../../state";
import { getSpread, getSpreadId, getSpreadVersion } from "./selector";
import {
  AccountId,
  RuleChangeLogItem,
  Spread,
  SpreadFinancialValue,
  SpreadSheetType
} from "./types";

import { TagAccount } from "../../chartOfAccounts/Tag/types";

const create = actionCreatorFactory("Spread");
const createAsync = asyncFactory<State>(create);

export interface LoadParams {
  id: string;
  isBankAnalyst: boolean;
  userId: string;
  environmentName?: string;
  status?: string;
}

interface LoadSuccessParams {
  spread: Spread;
  comments: Comment[];
  version: number;
}

export const loadSpread = createAsync<LoadParams, LoadSuccessParams>(
  "LOAD",
  async (params, dispatch, getState) => {
    dispatch({
      payload: { lastUsed: new Date().getTime().toString() },
      type: "UPDATE_LAST_USED"
    });
    const { spread, comments, version } = await api.spread.get(
      params.id,
      isCreditLensFeatureEnabled(getState()),
      params.isBankAnalyst,
      params.environmentName,
      params.status
    );
    return { spread, comments, version };
  }
);
const loadSpreadAction = thunkToAction(loadSpread.action);
export type LoadSpreadActionType = typeof loadSpreadAction;

export interface UpdateAccountParams {
  financialValueLens: CorrectLens<Spread, SpreadFinancialValue>;
  newValue: number | null;
  accountId: AccountId;
  period: number;
  spreadSheetType: SpreadSheetType;
  reason: string;
  periodString: string;
}

export interface UpdateSuccessParams {
  spread: Spread;
  version: number;
}

export const updateAccount = createAsync<
  UpdateAccountParams,
  UpdateSuccessParams
>("UPDATE_ACCOUNT", async (params, dispatch, getState) => {
  dispatch({
    payload: { lastUsed: new Date().getTime().toString() },
    type: "UPDATE_LAST_USED"
  });
  const state = getState();
  const initialSpread = getSpread(state);
  const userId = getUserId(state);

  if (initialSpread) {
    const periodString = params.periodString.replace(/\//g, "");
    const updatedFinancialValueModel: SpreadUpdateFinancialValueModel = {
      financialValueModelId: params.accountId + "__" + periodString,
      nodeId: params.accountId,
      period: params.periodString,
      userAdjustedValue: params.newValue
    };
    const { spread, version } = await api.spread.save(
      {
        spreadId: initialSpread.id,
        updatedFinancialValueModel,
        userId,
        reason: params.reason,
        version: getSpreadVersion(getState())
      },
      isCreditLensFeatureEnabled(getState())
    );
    return { spread, version };
  } else {
    throw Error("No spread loaded when account update attempted");
  }
});

const updateAccountAction = thunkToAction(updateAccount.action);
export type UpdateAccountActionType = typeof updateAccountAction;

interface UnmapHiddenAccountRowParams {
  spreadId: string;
  nodeId: string;
  userId: string;
}
interface UnmapHiddenAccountRowSuccessParams {
  spread: Spread;
  version: number;
}
export const unmapHiddenAccountRow = createAsync<
  UnmapHiddenAccountRowParams,
  UnmapHiddenAccountRowSuccessParams
>("UNMAP_HIDDEN_ACCOUNT_ROW", async (params, dispatch, getState) => {
  dispatch({
    payload: { lastUsed: new Date().getTime().toString() },
    type: "UPDATE_LAST_USED"
  });

  const { spread, version } = await api.rules.unmapAccount(
    params.spreadId,
    params.nodeId,
    isCreditLensFeatureEnabled(getState())
  );
  return { spread, version };
});
const unmapHiddenAccountRowAction = thunkToAction(unmapHiddenAccountRow.action);
export type UnmapHiddenAccountRowActionType = typeof unmapHiddenAccountRowAction;

export enum EXCEPTION_STATE {
  ACTIVATE = "ACTIVATE",
  DEACTIVATE = "DEACTIVATE"
}

export interface DocExceptionParams {
  spreadId: string;
  documentExceptionId: string;
  state: EXCEPTION_STATE;
}

export interface DocExceptionStateSuccess {
  spread: Spread;
  version: number;
}

export const updateDocumentExceptionState = createAsync<
  DocExceptionParams,
  DocExceptionStateSuccess
>("UPDATE_DOC_EXCEPTION_STATE", async (params, dispatch, getState) => {
  dispatch({
    payload: { lastUsed: new Date().getTime().toString() },
    type: "UPDATE_LAST_USED"
  });
  const { spread, version } = await api.updateDocumentExceptionState.post(
    {
      ...params,
      userId: getUserId(getState()),
      version: getSpreadVersion(getState())
    },
    isCreditLensFeatureEnabled(getState())
  );

  return { spread, version };
});
const updateDocumentExceptionStateAction = thunkToAction(
  updateDocumentExceptionState.action
);
export type UpdateDocumentExceptionStateActionType = typeof updateDocumentExceptionStateAction;

export interface UnlockSpreadParams {
  spreadId: string;
  environmentName?: string;
  status?: string;
  hasElasticSearchDelay?: boolean;
}

export const unlockSpread = createAsync<UnlockSpreadParams, {}>(
  "UNLOCK_SPREAD",
  async (params, dispatch, getState) => {
    await api.spread.unlock(
      params.spreadId,
      params.environmentName,
      params.status,
      params.hasElasticSearchDelay
    );
    return;
  }
);
const unlockSpreadAction = thunkToAction(unlockSpread.action);
export type UnlockSpreadActionType = typeof unlockSpreadAction;

export const uncacheSpread = createAsync<any, {}>(
  "UNCACHE_SPREAD",
  async (dispatch, getState) => {
    return;
  }
);
const uncacheSpreadAction = thunkToAction(uncacheSpread.action);
export type UncacheSpreadActionType = typeof uncacheSpreadAction;

export interface SpreadStateChangeModel {
  bankAnalyst: boolean;
  edit: boolean;
  environmentName?: string;
  managedService: boolean;
  reject: boolean;
  spreadId: string;
  version?: number;
  rulesToDelete?: RuleChangeLogItem[];
}

export interface SpreadStateChangeSuccess {
  newStatus?: BriefSpreadStatus | undefined;
  version: number;
}

export const changeSpreadState = createAsync<
  SpreadStateChangeModel,
  SpreadStateChangeSuccess
>("CHANGE_SPREAD_STATE", async (params, dispatch, getState) => {
  dispatch({
    payload: { lastUsed: new Date().getTime().toString() },
    type: "UPDATE_LAST_USED"
  });
  const isCreditLensUser = isCreditLensFeatureEnabled(getState());

  try {
    const response = await api.briefSpread.spreadStateChange(
      {
        ...params,
        version: isCreditLensUser ? -1 : getSpreadVersion(getState())
      },
      isCreditLensUser
    );

    const newStatus: SPREAD_STATUSES | undefined = response.spreadStatus
      ? response.spreadStatus
      : undefined;

    return { newStatus, version: response.version };
  } catch {
    throw Error("Couldn't update the status of the spread.");
  }
});
const changeSpreadStateAction = thunkToAction(changeSpreadState.action);
export type ChangeSpreadStateActionType = typeof changeSpreadStateAction;

export const enterMappingMode = create<string>("ENTER_MAPPING_MODE");

export const exitMappingMode = create("EXIT_MAPPING_MODE");

interface RemapAccountParams {
  spreadId: string;
  rule: RuleChangeLogItem;
}
interface RemapAccountSuccessParams {
  spread: Spread;
  version: number;
}
export const remapAccount = createAsync<
  RemapAccountParams,
  RemapAccountSuccessParams
>("REMAP_ACCOUNT", async (params, dispatch, getState) => {
  dispatch({
    payload: { lastUsed: new Date().getTime().toString() },
    type: "UPDATE_LAST_USED"
  });

  const { spread, version } = await api.rules.mapRule(
    params.spreadId,
    params.rule,
    isCreditLensFeatureEnabled(getState())
  );
  return { spread, version };
});
const remapAccountAction = thunkToAction(remapAccount.action);
export type RemapAccountActionType = typeof remapAccountAction;

interface MapUnmappedAccountParams {
  spreadId: string;
  unmappedAccountId: string;
  nodeId: string;
}
interface MapUnmappedAccountSuccessParams {
  spread: Spread;
  version: number;
}
export const mapUnmappedAccount = createAsync<
  MapUnmappedAccountParams,
  MapUnmappedAccountSuccessParams
>("MAP_UNMAPPED_ACCOUNT", async (params, dispatch, getState) => {
  dispatch({
    payload: { lastUsed: new Date().getTime().toString() },
    type: "UPDATE_LAST_USED"
  });

  const { spread, version } = await api.rules.mapUnmappedAccount(
    params.spreadId,
    params.unmappedAccountId,
    params.nodeId,
    isCreditLensFeatureEnabled(getState())
  );
  return { spread, version };
});
const mapUnmappedAccountAction = thunkToAction(mapUnmappedAccount.action);
export type MapUnmappedAccountActionType = typeof mapUnmappedAccountAction;

interface DeleteRulesParams {
  spreadId?: string;
  rulesToDelete: RuleChangeLogItem[];
}

export const deleteRules = createAsync<DeleteRulesParams, {}>(
  "DELETE_RULES",
  async (params, dispatch, getState) => {
    dispatch({
      payload: { lastUsed: new Date().getTime().toString() },
      type: "UPDATE_LAST_USED"
    });

    await api.rules.deleteRules(params.rulesToDelete, params.spreadId);
    return;
  }
);
const deleteRulesAction = thunkToAction(deleteRules.action);
export type DeleteRulesActionType = typeof deleteRulesAction;

interface UndoRuleParams {
  spreadId: string;
  currentRule: RuleChangeLogItem;
  previousRule: RuleChangeLogItem;
}

interface UndoRuleSuccessParams {
  spread: Spread;
  version: number;
}

interface TagAccountSuccessParams {
  spread: Spread;
  version: number;
}

export const undoRule = createAsync<UndoRuleParams, UndoRuleSuccessParams>(
  "UNDO_RULE",
  async (params, dispatch, getState) => {
    dispatch({
      payload: { lastUsed: new Date().getTime().toString() },
      type: "UPDATE_LAST_USED"
    });

    const { spread, version } = await api.rules.undoRule(
      params.spreadId,
      params.currentRule,
      params.previousRule,
      isCreditLensFeatureEnabled(getState())
    );
    return { spread, version };
  }
);

const undoRuleAction = thunkToAction(undoRule.action);
export type UndoRuleActionType = typeof undoRuleAction;

export const tagAccount = createAsync<TagAccount, TagAccountSuccessParams>(
  "TAG_ACCOUNT",
  async (params, dispatch, getState) => {
    dispatch({
      payload: { lastUsed: new Date().getTime().toString() },
      type: "UPDATE_LAST_USED"
    });

    const state = getState();
    const spreadId = getSpreadId(state);
    const isCreditLens = isCreditLensFeatureEnabled(getState());

    const { spread, version } = await api.tag.save(
      params,
      spreadId,
      isCreditLens
    );
    return { spread, version };
  }
);

const saveTagAction = thunkToAction(tagAccount.action);
export type SaveTagActionType = typeof saveTagAction;

export interface ExportReportParams {
  spreadId: string;
  sheetPerPeriod?: boolean;
}

export const exportReport = createAsync<ExportReportParams, {}>(
  "EXPORT_REPORT",
  async (params, dispatch, getState) => {
    const sheetPerPeriod = params.sheetPerPeriod ? true : false;
    await api.spread.exportReport(params.spreadId, sheetPerPeriod);
  }
);

const exportReportAction = thunkToAction(exportReport.action);
export type ExportReportActionType = typeof exportReportAction;

interface RejectSpreadParams {
  spreadId: string;
  reason: any;
  periods: string[];
  isCreditLens: boolean;
  commentText?: string;
}

export const rejectSpread = createAsync<RejectSpreadParams, {}>(
  "REJECT_SPREAD",
  async (params, dispatch, getState) => {
    await api.spread.reject(
      params.spreadId,
      params.reason,
      params.periods,
      isCreditLensFeatureEnabled(getState()),
      params.commentText
    );

    return params;
  }
);

const rejectSpreadAction = thunkToAction(rejectSpread.action);
export type rejectSpreadActionType = typeof rejectSpreadAction;



// WEBPACK FOOTER //
// ./src/spread/model/spread/actions.ts